import type { SensorDoc as SharedSensorDoc, UnknownSensorDoc as SharedUnknownSensorDoc } from '@jums-dashboard/shared';
import {
  collection,
  doc,
  type CollectionReference,
  type DocumentReference,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type Timestamp,
} from 'firebase/firestore';

import type { WidgetConfigDoc } from '@/features/dashboard/widgetConfig';
import type { HomeDoc } from '@/features/homeAutomation/home';
import { firestore } from '@/services/firebase';

type SensorDoc = SharedSensorDoc<Timestamp>;
type UnknownSensorDoc = SharedUnknownSensorDoc<Timestamp>;

const widgetConverter: FirestoreDataConverter<WidgetConfigDoc> = {
  toFirestore(data: WidgetConfigDoc) {
    return data;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot): WidgetConfigDoc {
    return snapshot.data() as WidgetConfigDoc;
  },
};

export function userWidgetsCollection(uid: string): CollectionReference<WidgetConfigDoc> {
  return collection(firestore, 'users', uid, 'widgets').withConverter(widgetConverter);
}

export function userWidgetDoc(uid: string, widgetId: string): DocumentReference<WidgetConfigDoc> {
  return doc(firestore, 'users', uid, 'widgets', widgetId).withConverter(widgetConverter);
}

const homeConverter: FirestoreDataConverter<HomeDoc> = {
  toFirestore(data: HomeDoc) {
    return data;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot): HomeDoc {
    return snapshot.data() as HomeDoc;
  },
};

export function homesCollection(): CollectionReference<HomeDoc> {
  return collection(firestore, 'homes').withConverter(homeConverter);
}

export function homeDoc(homeId: string): DocumentReference<HomeDoc> {
  return doc(firestore, 'homes', homeId).withConverter(homeConverter);
}

const unknownSensorConverter: FirestoreDataConverter<UnknownSensorDoc> = {
  toFirestore(data: UnknownSensorDoc) {
    return data;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot): UnknownSensorDoc {
    return snapshot.data() as UnknownSensorDoc;
  },
};

export function unknownSensorsCollection(homeId: string): CollectionReference<UnknownSensorDoc> {
  return collection(firestore, 'homes', homeId, 'unknownSensors').withConverter(unknownSensorConverter);
}

export function unknownSensorDoc(homeId: string, physicalId: string): DocumentReference<UnknownSensorDoc> {
  return doc(firestore, 'homes', homeId, 'unknownSensors', physicalId).withConverter(unknownSensorConverter);
}

const sensorConverter: FirestoreDataConverter<SensorDoc> = {
  toFirestore(data: SensorDoc) {
    return data;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot): SensorDoc {
    return snapshot.data() as SensorDoc;
  },
};

export function sensorsCollection(homeId: string): CollectionReference<SensorDoc> {
  return collection(firestore, 'homes', homeId, 'sensors').withConverter(sensorConverter);
}

export function sensorDoc(homeId: string, sensorId: string): DocumentReference<SensorDoc> {
  return doc(firestore, 'homes', homeId, 'sensors', sensorId).withConverter(sensorConverter);
}
